import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { IssueLicense } from "../components/IssueLicense";
import { useAuth } from "../lib/auth";
import type { LicenseSummary, Page } from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "../components/ui";

const STATUSES = ["trialing", "active", "past_due", "suspended", "revoked", "expired"] as const;

export function Licenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);

  const params = new URLSearchParams();
  if (search.trim().length > 0) params.set("search", search.trim());
  if (status.length > 0) params.set("status", status);
  if (cursor !== null) params.set("cursor", cursor);

  const licenses = useQuery({
    queryKey: ["licenses", params.toString()],
    queryFn: () => api<Page<LicenseSummary>>(`/api/v1/admin/licenses?${params.toString()}`),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={isAdmin ? "Licences" : "Your licences"}
        lede={
          isAdmin
            ? "Every licence this platform has issued."
            : "Licences issued to your account, and where they are running."
        }
        action={
          isAdmin ? (
            <Button variant="primary" onClick={() => setIssuing(true)}>
              Issue licence
            </Button>
          ) : undefined
        }
      />

      {/* The search field asked for 16rem it could not have on a phone, which
          pushed the whole page sideways. Stacked below the breakpoint, side by
          side above it. */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem]">
        <Field label="Search">
          <Input
            value={search}
            placeholder="Customer name, email, or last four symbols"
            onChange={(event) => {
              setSearch(event.target.value);
              setCursor(null);
            }}
          />
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setCursor(null);
            }}
          >
            <option value="">All</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value.replace("_", " ")}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Card>
        {licenses.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : licenses.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load licences. Retry from the browser reload.</ErrorNote>
          </div>
        ) : licenses.data.items.length === 0 ? (
          <EmptyState>
            {search.length > 0 || status.length > 0
              ? "No licences match those filters."
              : "No licences yet."}
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Key</Th>
                  <Th>Customer</Th>
                  <Th>Product</Th>
                  <Th>Plan</Th>
                  <Th>Status</Th>
                  <Th>Seats</Th>
                  <Th>Expires</Th>
                </tr>
              </thead>
              <tbody>
                {licenses.data.items.map((license) => (
                  <tr key={license.id} className="border-b border-hairline last:border-0">
                    <Td label="Key">
                      <Link
                        to={`/licenses/${license.id}`}
                        className="tabular text-accent underline-offset-4 hover:underline"
                      >
                        {license.product.code}-••••-{license.keyLast4}
                      </Link>
                    </Td>
                    <Td label="Customer">
                      {/* One flex item once the row is a card, so the name and
                          the address stay stacked rather than sitting apart. */}
                      <span className="block">
                        <span className="block">{license.customer.name}</span>
                        <span className="block text-xs text-dim">{license.customer.email}</span>
                      </span>
                    </Td>
                    <Td label="Product" className="text-muted">
                      {license.product.name}
                    </Td>
                    <Td label="Plan" className="text-muted">
                      {license.plan.name}
                    </Td>
                    <Td label="Status">
                      <StatusBadge status={license.status} />
                    </Td>
                    <Td label="Seats" className="tabular text-muted">
                      {license._count?.activations ?? 0} /{" "}
                      {license.maxActivations ?? license.plan.maxActivations}
                    </Td>
                    <Td label="Expires" className="text-muted">
                      {formatDate(license.expiresAt)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {(cursor !== null || licenses.data?.nextCursor != null) && (
        <div className="flex justify-between">
          <Button onClick={() => setCursor(null)} disabled={cursor === null}>
            First page
          </Button>
          <Button
            onClick={() => setCursor(licenses.data?.nextCursor ?? null)}
            disabled={licenses.data?.nextCursor == null}
          >
            Next page
          </Button>
        </div>
      )}

      {issuing && <IssueLicense onClose={() => setIssuing(false)} />}
    </div>
  );
}

/**
 * The key is returned once and never again — only its HMAC is stored. The panel
 * stays open on success and says so plainly, because a dialog that closes
 * itself here loses the only copy that will ever exist.
 */
